/**
 * PaginationFooter — the ruled Prev/Next paging row, extracted verbatim
 * from CompletedView (contract item 5) so every paged list renders the
 * same control: `Showing X–Y of N` off the server's honest total, Prev
 * and Next dead at the ends.
 *
 * The separator is an EN dash, matching the pattern this was extracted
 * from. `total` is the SERVER's count of the filtered set, never a tally
 * of the rows on the page: a footer fed rows.length claims the page is
 * the world, which is the exact truncation this control exists to name.
 *
 * `testIdPrefix` keeps each surface's pinned testids intact:
 * CompletedView's `completed-` and the invoice list's `invoices-page-`.
 */

import { type ReactNode } from 'react';

export interface PaginationFooterProps {
  offset: number;
  pageSize: number;
  /** The server's total for the filtered set. */
  total: number;
  onOffsetChange: (offset: number) => void;
  /** Prefixes `count`, `prev` and `next` testids. */
  testIdPrefix: string;
  /** Container classes. The default rides the six-role type scale
   *  (marker step); CompletedView passes its own grandfathered classes so
   *  the extraction changes nothing it renders. */
  className?: string;
}

export function PaginationFooter({
  offset,
  pageSize,
  total,
  onOffsetChange,
  testIdPrefix,
  className = 'flex items-center gap-3 mt-3 text-xs text-sage-400',
}: PaginationFooterProps): ReactNode {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + pageSize, total);
  const button =
    'px-2.5 py-1 rounded-base border border-sage-300 disabled:opacity-40 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

  return (
    <div className={className}>
      <span data-testid={`${testIdPrefix}count`}>
        Showing {from}–{to} of {total}
      </span>
      <div className="ml-auto flex gap-2">
        <button
          type="button"
          data-testid={`${testIdPrefix}prev`}
          disabled={offset === 0}
          onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
          className={button}
        >
          Prev
        </button>
        <button
          type="button"
          data-testid={`${testIdPrefix}next`}
          disabled={offset + pageSize >= total}
          onClick={() => onOffsetChange(offset + pageSize)}
          className={button}
        >
          Next
        </button>
      </div>
    </div>
  );
}
